import { Link } from 'react-router-dom';
import { ExternalLink, GitBranch, Play, Eye, Loader2 } from 'lucide-react';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Project } from '@/types/project';
import { useDeployments } from '@/hooks/queries/useDeployments';

interface ProjectCardProps {
  project: Project;
}

export default function ProjectCard({ project }: ProjectCardProps) {
  const { data: deployments, isLoading: isLoadingDeployments } = useDeployments(project.id);
  
  // Calculate successful deployment directly from deployments data
  const latestSuccessfulDeployment = deployments?.find((d) => d.status === 'success') || null;

  const handleViewClick = () => {
    if (latestSuccessfulDeployment?.preview_url) {
      // Check if URL already has protocol
      const url = latestSuccessfulDeployment.preview_url.startsWith('http://') || 
                  latestSuccessfulDeployment.preview_url.startsWith('https://')
        ? latestSuccessfulDeployment.preview_url
        : `http://${latestSuccessfulDeployment.preview_url}`;
      
      console.log('[View Button] Opening URL:', url);
      window.open(url, '_blank');
    } else {
      console.log('[View Button] No preview URL available');
    }
  };

  const getViewButtonState = () => {
    if (isLoadingDeployments) {
      return {
        disabled: true,
        text: 'Loading...',
        icon: <Loader2 className="h-3 w-3 animate-spin" />,
        title: '',
      };
    }

    // PRIORITY 1: If there's a successful deployment, enable View button
    if (latestSuccessfulDeployment) {
      return {
        disabled: false,
        text: 'View',
        icon: <Eye className="h-3 w-3" />,
        title: 'Open deployed site',
      };
    }

    // PRIORITY 2: Check if there are building/pending deployments (but no success)
    const hasBuilding = deployments?.some((d) => d.status === 'building' || d.status === 'pending');

    if (hasBuilding) {
      return {
        disabled: true,
        text: 'Building...',
        icon: <Loader2 className="h-3 w-3 animate-spin" />,
        title: 'Build in progress',
      };
    }

    // PRIORITY 3: Check if there are only failed deployments
    const hasFailed = deployments?.some((d) => d.status === 'failed');

    if (hasFailed) {
      return {
        disabled: true,
        text: 'Build Failed',
        icon: null,
        title: 'Last build failed',
      };
    }

    // PRIORITY 4: No deployments at all
    return {
      disabled: true,
      text: 'No Deployments',
      icon: null,
      title: 'Trigger a build first',
    };
  };

  const viewButtonState = getViewButtonState();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          📦 {project.name}
          {latestSuccessfulDeployment && (
            <Badge className="text-xs bg-green-500 hover:bg-green-600">
              Live
            </Badge>
          )}
        </CardTitle>
        <a
          href={project.github_url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-blue-600 hover:underline flex items-center gap-1"
        >
          <GitBranch className="h-3 w-3" />
          {project.github_url.replace('https://github.com/', '')}
          <ExternalLink className="h-3 w-3" />
        </a>
      </CardHeader>

      <CardContent>
        {latestSuccessfulDeployment ? (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <span className="font-medium">Preview:</span>
              <code className="text-xs bg-gray-100 px-2 py-1 rounded truncate">
                {latestSuccessfulDeployment.preview_url}
              </code>
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-500">
            {isLoadingDeployments ? 'Checking deployments...' : 'No successful deployment yet'}
          </p>
        )}
      </CardContent>

      <CardFooter className="gap-2">
        <Button
          variant="outline"
          size="sm"
          className="flex-1"
          onClick={handleViewClick}
          disabled={viewButtonState.disabled}
          title={viewButtonState.title}
        >
          {viewButtonState.icon}
          <span className={viewButtonState.icon ? 'ml-1' : ''}>
            {viewButtonState.text}
          </span>
        </Button>
        {latestSuccessfulDeployment ? (
          <Link 
            to={`/projects/${project.id}/deployments/${latestSuccessfulDeployment.id}`} 
            className="flex-1"
          >
            <Button variant="outline" size="sm" className="w-full">
              Details
            </Button>
          </Link>
        ) : (
          <Button variant="outline" size="sm" className="flex-1" disabled>
            Details
          </Button>
        )}
        <Link to={`/projects/${project.id}/deploy`} className="flex-1">
          <Button size="sm" className="w-full">
            <Play className="mr-1 h-3 w-3" />
            Deploy
          </Button>
        </Link>
      </CardFooter>
    </Card>
  );
}
